import { TrashIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import Tooltip from 'components/layout/tooltip';
import EquipmentDetailsCard from 'components/structure-page/equipment-details-card';
import EquipmentStepRecommendations from 'components/structure-page/equipment-step-recommendations';
import ExistingEquipmentList from 'components/structure-page/lists/existing-equipment-list';
import { ExistingSystemType } from 'data/types';
import useRemoveExistingSystem from 'hooks/use-remove-existing-system';
import { ReactNode } from 'react';

type ExistingSystemSectionProps = {
  system: ExistingSystemType;
  showHeader: boolean;
  removable: boolean;
  awaitingFirstSave: boolean;
  preparing: ReactNode;
};

export default function ExistingSystemSection({
  system,
  showHeader,
  removable,
  awaitingFirstSave,
  preparing
}: ExistingSystemSectionProps) {
  const removeSystem = useRemoveExistingSystem();

  return (
    <section
      data-testid={`existing-system-section-${system.uniqueId}`}
      className={clsx(
        'flex flex-col gap-4',
        showHeader && 'rounded-xl border border-gray-200 bg-gray-50 p-4'
      )}
    >
      {showHeader && (
        <div className='flex items-center justify-between'>
          <div className='text-sm font-semibold text-gray-700'>
            {system.name}
          </div>
          {removable && (
            <button
              type='button'
              aria-label={`Remove ${system.name}`}
              className='relative cursor-pointer text-gray-500 hover:text-red-600'
              onClick={() => removeSystem(system)}
            >
              <TrashIcon className='h-5 w-5' />
              <Tooltip text='Remove this system' />
            </button>
          )}
        </div>
      )}
      <ExistingEquipmentList system={system} />
      {awaitingFirstSave ? (
        preparing
      ) : (
        <EquipmentStepRecommendations system={system} visibleSection='have' />
      )}
      {system.components.map((component) => (
        <EquipmentDetailsCard key={component.uniqueId} component={component} />
      ))}
    </section>
  );
}
